import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link2, TrendingUp, TrendingDown, Download, Calendar, Loader2, Award, AlertTriangle, BarChart3, MousePointer, Eye, Target, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  ComposedChart
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600" },
    purple: { bg: "bg-purple-50", text: "text-purple-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-600" },
    red: { bg: "bg-red-50", text: "text-red-600" }
  };
  const colors = colorClasses[color];

  return (
    <Card className="stat-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

// Month Comparison Card
const MonthComparisonCard = ({ title, bestMonth, worstMonth, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600" },
    amber: { bg: "bg-amber-50", text: "text-amber-600" }
  };
  const colors = colorClasses[color];

  return (
    <Card className="stat-card">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${colors.bg}`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <p className="font-semibold text-slate-700">{title}</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">Melhor mês</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-emerald-700">{bestMonth?.month || '-'}</p>
              <p className="text-xs text-emerald-600">{bestMonth?.value || 0}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">Pior mês</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-red-700">{worstMonth?.month || '-'}</p>
              <p className="text-xs text-red-600">{worstMonth?.value || 0}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SEOAnnualReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/seo`);
      setReports(response.data);
      
      // Extract available years
      const years = [...new Set(response.data.map(r => new Date(r.period_start).getFullYear()))];
      setAvailableYears(years.sort((a, b) => b - a));
      if (years.length > 0 && !years.includes(selectedYear)) {
        setSelectedYear(years[0]);
      }
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
    } finally {
      setLoading(false);
    }
  };

  // Filter reports by selected year
  const yearReports = reports.filter(r => {
    const year = new Date(r.period_start).getFullYear();
    return year === selectedYear;
  });

  // Aggregate data by month
  const monthlyData = MONTHS_PT.map((month, index) => {
    const monthReports = yearReports.filter(r => {
      const reportMonth = new Date(r.period_start).getMonth();
      return reportMonth === index;
    });

    if (monthReports.length === 0) {
      return { month, backlinks: 0, da: 0, domains: 0, mentions: 0, clicks: 0, impressions: 0, ctr: 0, position: 0 };
    }

    // Sum/average values for the month
    const totals = monthReports.reduce((acc, r) => ({
      backlinks: acc.backlinks + (r.total_backlinks_acquired || 0),
      da: acc.da + (r.avg_domain_authority || 0),
      domains: acc.domains + (r.new_referring_domains || 0),
      mentions: acc.mentions + (r.brand_mentions || 0),
      clicks: acc.clicks + (r.clicks || 0),
      impressions: acc.impressions + (r.impressions || 0),
      ctr: acc.ctr + (r.avg_ctr || 0),
      position: acc.position + (r.avg_position || 0)
    }), { backlinks: 0, da: 0, domains: 0, mentions: 0, clicks: 0, impressions: 0, ctr: 0, position: 0 });

    return {
      month,
      backlinks: totals.backlinks,
      da: totals.da / monthReports.length,
      domains: totals.domains,
      mentions: totals.mentions,
      clicks: totals.clicks,
      impressions: totals.impressions,
      ctr: totals.ctr / monthReports.length,
      position: totals.position / monthReports.length
    };
  });

  // Calculate annual totals
  const annualTotals = {
    backlinks: monthlyData.reduce((sum, m) => sum + m.backlinks, 0),
    domains: monthlyData.reduce((sum, m) => sum + m.domains, 0),
    mentions: monthlyData.reduce((sum, m) => sum + m.mentions, 0),
    clicks: monthlyData.reduce((sum, m) => sum + m.clicks, 0),
    impressions: monthlyData.reduce((sum, m) => sum + m.impressions, 0),
    avgDa: monthlyData.filter(m => m.da > 0).reduce((sum, m) => sum + m.da, 0) / (monthlyData.filter(m => m.da > 0).length || 1),
    avgCtr: monthlyData.filter(m => m.ctr > 0).reduce((sum, m) => sum + m.ctr, 0) / (monthlyData.filter(m => m.ctr > 0).length || 1),
    avgPosition: monthlyData.filter(m => m.position > 0).reduce((sum, m) => sum + m.position, 0) / (monthlyData.filter(m => m.position > 0).length || 1)
  };

  // Find best/worst months
  const findBestWorst = (data, key, higherIsBetter = true) => {
    const filtered = data.filter(m => m[key] > 0);
    if (filtered.length === 0) return { best: null, worst: null };
    
    const sorted = [...filtered].sort((a, b) => higherIsBetter ? b[key] - a[key] : a[key] - b[key]);
    return {
      best: { month: sorted[0].month, value: sorted[0][key].toLocaleString('pt-PT') },
      worst: { month: sorted[sorted.length - 1].month, value: sorted[sorted.length - 1][key].toLocaleString('pt-PT') }
    };
  };

  const backlinksComparison = findBestWorst(monthlyData, 'backlinks');
  const clicksComparison = findBestWorst(monthlyData, 'clicks');
  const positionComparison = findBestWorst(monthlyData, 'position', false); // Lower is better

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/export/seo-annual/${user.client_id}?year=${selectedYear}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-seo-anual-${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="seo-annual-report-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <div className="p-2 bg-blue-100 rounded-xl">
              <Link2 className="w-7 h-7 text-blue-600" />
            </div>
            SEO - Relatório Anual
          </h1>
          <p className="text-slate-500 mt-2">
            Análise completa de performance SEO ao longo do ano
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 font-medium"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <Button 
            onClick={handleExportPDF}
            disabled={exporting || yearReports.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar PDF
          </Button>
        </div>
      </div>

      {yearReports.length === 0 ? (
        <Card className="stat-card">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Sem dados para {selectedYear}</h3>
            <p className="text-slate-500">Não existem relatórios de SEO para este ano.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Annual Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Resumo Anual {selectedYear}</h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Backlinks"
                value={annualTotals.backlinks.toLocaleString('pt-PT')}
                subtitle="Acumulado anual"
                icon={Link2}
                color="blue"
              />
              <StatCard
                title="DA Médio"
                value={annualTotals.avgDa.toFixed(1)}
                subtitle="Média anual"
                icon={BarChart3}
                color="green"
              />
              <StatCard
                title="Total Cliques"
                value={annualTotals.clicks.toLocaleString('pt-PT')}
                subtitle="Acumulado anual"
                icon={MousePointer}
                color="purple"
              />
              <StatCard
                title="Total Impressões"
                value={annualTotals.impressions.toLocaleString('pt-PT')}
                subtitle="Acumulado anual"
                icon={Eye}
                color="amber"
              />
            </div>
          </div>

          {/* Best/Worst Months */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Comparação Mensal</h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MonthComparisonCard
                title="Backlinks"
                bestMonth={backlinksComparison.best}
                worstMonth={backlinksComparison.worst}
                icon={Link2}
                color="blue"
              />
              <MonthComparisonCard
                title="Cliques"
                bestMonth={clicksComparison.best}
                worstMonth={clicksComparison.worst}
                icon={MousePointer}
                color="green"
              />
              <MonthComparisonCard
                title="Posição Média"
                bestMonth={positionComparison.worst}
                worstMonth={positionComparison.best}
                icon={Target}
                color="amber"
              />
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Backlinks Evolution */}
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Link2 className="w-5 h-5 text-blue-600" />
                  Evolução de Backlinks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="backlinks" fill="#2563EB" name="Backlinks" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="domains" stroke="#10B981" strokeWidth={2} name="Novos Domínios" dot={{ fill: '#10B981' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Clicks & Impressions */}
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MousePointer className="w-5 h-5 text-purple-600" />
                  Cliques e Impressões
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#7C3AED" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="clicks" stroke="#7C3AED" strokeWidth={2} fill="url(#colorClicks)" name="Cliques" />
                    <Area type="monotone" dataKey="impressions" stroke="#F59E0B" strokeWidth={2} fill="url(#colorImpressions)" name="Impressões" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* CTR & Position */}
            <Card className="stat-card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-emerald-600" />
                  CTR e Posição Média
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis yAxisId="left" stroke="#10B981" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" stroke="#EF4444" fontSize={12} reversed />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value, name) => [
                        name === 'CTR (%)' ? `${value.toFixed(2)}%` : value.toFixed(1),
                        name
                      ]}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="ctr" stroke="#10B981" strokeWidth={2} name="CTR (%)" dot={{ fill: '#10B981' }} />
                    <Line yAxisId="right" type="monotone" dataKey="position" stroke="#EF4444" strokeWidth={2} name="Posição Média" dot={{ fill: '#EF4444' }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Table */}
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-slate-600" />
                Dados Mensais Detalhados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Mês</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Backlinks</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">DA Médio</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Novos Domínios</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Cliques</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Impressões</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">CTR</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Posição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((data, index) => (
                      <tr key={data.month} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                        <td className="py-3 px-4 font-medium text-slate-700">{data.month}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.backlinks.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.da > 0 ? data.da.toFixed(1) : '-'}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.domains.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.clicks.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.impressions.toLocaleString('pt-PT')}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.ctr > 0 ? `${data.ctr.toFixed(2)}%` : '-'}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{data.position > 0 ? data.position.toFixed(1) : '-'}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 font-semibold">
                      <td className="py-3 px-4 text-blue-700">TOTAL/MÉDIA</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.backlinks.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.avgDa.toFixed(1)}</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.domains.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.clicks.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.impressions.toLocaleString('pt-PT')}</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.avgCtr.toFixed(2)}%</td>
                      <td className="text-right py-3 px-4 text-blue-700">{annualTotals.avgPosition.toFixed(1)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SEOAnnualReportPage;
